import { Building2 } from 'lucide-react'

import { useBranchSelection } from '@/components/app-shell/use-branch-selection'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { branchLabel } from '@/lib/branch'

/**
 * The branch the operator is working in, as the wide top bar shows it.
 *
 * Most staff hold exactly one branch and see a static label — there is nothing to choose, and a
 * dropdown with one entry is an invitation to wonder what else is in it. Head-office and audit
 * roles hold several.
 *
 * Below `lg` the top bar has no room for this and the same choice is offered by
 * <HeaderContextMenu> instead; both drive `useBranchSelection`.
 */
export function BranchSelector() {
  const { branches, isPending, selectedBranchId, currentBranch, canSwitch, selectBranch } =
    useBranchSelection()

  if (isPending) {
    return <Skeleton className="h-8 w-40" />
  }

  if (!canSwitch) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-body">
        <Building2 className="size-4 text-muted" aria-hidden />
        <span className="font-medium text-heading">{branchLabel(currentBranch)}</span>
        {currentBranch ? <span className="font-mono text-xs text-muted">{currentBranch.code}</span> : null}
      </span>
    )
  }

  return (
    <Select value={selectedBranchId ?? undefined} onValueChange={selectBranch}>
      <SelectTrigger className="h-8 w-56" aria-label="Operating branch">
        <span className="flex items-center gap-1.5 truncate">
          <Building2 className="size-4 shrink-0 text-muted" aria-hidden />
          <SelectValue placeholder="Select branch" />
        </span>
      </SelectTrigger>
      <SelectContent>
        {branches.map((branch) => (
          <SelectItem key={branch.id} value={branch.id}>
            <span className="font-mono text-xs text-muted">{branchLabel(branch)}</span>
            {branch.name ? ` ${branch.name}` : null}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
